import { ReferenceManyField } from 'react-admin';

import { GetB2bFullyManagedPlanContributionsOneResponse } from '@Common/types/b2b/orders';

import { b2bFullyManagedPlanContributionsStatusChoices } from '@sh4res/bo-common/choices/orders';
import { DatagridTyped, Pagination } from '@sh4res/bo-common/components';
import { CurrencyField, IdField, SelectChipField } from '@sh4res/bo-common/fields';
import { ResourceBasePath } from '@sh4res/bo-common/hooks';
import type { ColumnList } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type PayoutItem = Pick<
  GetB2bFullyManagedPlanContributionsOneResponse,
  'id' | 'total_gross_amount' | 'user_currency_amount' | 'tax_amount' | 'status'
>;

const testId = 'BoExternalB2bSavingsFundRequestsFullyManagedContributionsList';

const displayedColumns: (keyof PayoutItem)[] = isBoExternal()
  ? ['total_gross_amount', 'user_currency_amount', 'tax_amount', 'status']
  : ['id', 'total_gross_amount', 'user_currency_amount', 'tax_amount', 'status'];

export const FullyManagedContributionsList = ({
  resourceBasePath,
}: {
  resourceBasePath: ResourceBasePath;
}) => {
  const showId = !isBoExternal();

  const columnRegistry = {
    ...(showId
      ? {
          id: (
            <IdField
              reference={`${resourceBasePath}orders/fully_managed_plan_contributions`}
              label="resources.bo_common.b2b.savings_funds_requests.fully_managed_plan_contributions.fields.id"
              data-testid={`${testId}_IdField`}
            />
          ),
        }
      : {}),
    user_currency_amount: (
      <CurrencyField
        currency="user_currency"
        label="resources.bo_common.b2b.savings_funds_requests.fully_managed_plan_contributions.fields.user_currency_amount"
        testId={`${testId}_UserCurrencyAmountField`}
      />
    ),
    total_gross_amount: (
      <CurrencyField
        currency="user_currency"
        label="resources.bo_common.b2b.savings_funds_requests.fully_managed_plan_contributions.fields.total_gross_amount"
        testId={`${testId}_TotalGrossAmountField`}
      />
    ),
    tax_amount: (
      <CurrencyField
        currency="user_currency"
        label="resources.bo_common.b2b.savings_funds_requests.fully_managed_plan_contributions.fields.tax_amount"
        testId={`${testId}_TaxAmountField`}
      />
    ),
    status: (
      <SelectChipField
        choices={b2bFullyManagedPlanContributionsStatusChoices}
        label="resources.bo_common.b2b.savings_funds_requests.fully_managed_plan_contributions.fields.status"
        data-testid={`${testId}_StatusField`}
      />
    ),
  };

  return (
    <ReferenceManyField
      data-testid={`${testId}_ContributionsList`}
      reference={`${resourceBasePath}orders/fully_managed_plan_contributions`}
      target="savings_funds_request_id"
      perPage={10}
      filter={{ status: ['pending', 'paid', 'funding_failed'] }}
      pagination={<Pagination />}>
      <DatagridTyped<PayoutItem>
        rowClick={showId ? 'show' : false}
        sortableColumns={[]}
        columnRegistry={columnRegistry as ColumnList<PayoutItem>}
        displayedColumns={displayedColumns}
        bulkActionButtons={false}
        showCard={true}
      />
    </ReferenceManyField>
  );
};
